/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

/**
 * Canonical option vocabularies for the patient-intake assessment (BRD intake
 * flow, ported from the temp build's 8-page form). Values are stable kebab/slug
 * forms — labels are rendered on the FE via i18n keyed by these values, so the
 * stored data and the generated PDF never embed a display string.
 */

/** Tri-state used by most "does your child…" questions (matches the YesNo control). */
export const INTAKE_TRISTATE = ['yes', 'no', 'sometimes'] as const;
export type IntakeTriState = (typeof INTAKE_TRISTATE)[number];

/** Section 1 — child gender. */
export const INTAKE_GENDERS = ['male', 'female', 'other'] as const;
export type IntakeGender = (typeof INTAKE_GENDERS)[number];

/** Section 2 — pregnancy term. */
export const PREGNANCY_DURATIONS = ['full-term', 'pre-term'] as const;

/** Section 2 — mode of delivery. */
export const DELIVERY_TYPES = ['normal', 'c-section', 'breech', 'forceps'] as const;

/** Section 2 — how soon the child cried after birth. */
export const CRY_AFTER_BIRTH = ['immediately', 'delayed'] as const;

/** Section 5 — six gross-motor skills (checkbox grid → per-skill boolean). */
export const GROSS_MOTOR_SKILLS = [
  'jumping',
  'hopping',
  'throwing-catching',
  'climbing-stairs',
  'standing-one-leg',
  'alternate-step-climbing',
] as const;

/** Section 5 — six fine-motor skills. */
export const FINE_MOTOR_SKILLS = [
  'buttoning',
  'lacing',
  'tripod-grasp',
  'writing',
  'scissor-cutting',
  'colouring-in-boundaries',
] as const;

/** Section 5 — safety-awareness answer set (adds "not-sure" to the tri-state). */
export const SAFETY_AWARENESS = ['yes', 'no', 'sometimes', 'not-sure'] as const;

/** Section 5 — the five safety-awareness questions (keys stored in JSONB). */
export const SAFETY_QUESTION_KEYS = [
  'strangers',
  'traffic',
  'hazards',
  'wandering',
  'mouthing',
] as const;

/** Section 5 — special diet. */
export const SPECIAL_DIETS = [
  'regular',
  'vegetarian',
  'gluten-free',
  'dairy-free',
  'other',
] as const;

/** Section 6 — typical time to finish a meal. */
export const MEAL_COMPLETION_TIMES = ['20-30', '30-40', '40-50', '50-60', '60-plus'] as const;

/** Section 6 — food textures the child eats (multi-select). */
export const FOOD_TEXTURES = ['smooth-puree', 'soft-solids', 'hard-solids', 'tough-chewy'] as const;

/** Section 6 — toileting independence. */
export const TOILETING_SKILLS = [
  'independent',
  'reminders',
  'physical-help',
  'not-trained',
] as const;

/** Section 6 — self-care independence levels (brushing / bathing share this set). */
export const SELFCARE_LEVELS = [
  'independent',
  'supervision',
  'partial-help',
  'fully-dependent',
] as const;

/** Section 6 — dressing independence. */
export const DRESSING_SKILLS = [
  'independent',
  'some-help',
  'most-help',
  'fully-dependent',
] as const;

/** Section 7 — ways the child communicates (multi-select). */
export const COMMUNICATION_METHODS = [
  'crying',
  'taking-to-object',
  'placing-hand',
  'grabbing',
  'pointing',
  'naming',
  'two-word',
  'three-four-word',
  'sign-language',
] as const;
